"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { 
  User, 
  UserCredential,
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  deleteUser,
  updateEmail,
  updatePassword,
  getIdToken
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
// Import all available items from firebase
import { auth, app, isUsingMockAuth } from "../lib/firebase";

// Define a more robust interface for auth context
interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: Error | null;
  isUsingMockAuth: boolean;
  signIn: (email: string, password: string) => Promise<UserCredential>;
  signUp: (email: string, password: string) => Promise<UserCredential>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (user: User, displayName?: string, photoURL?: string) => Promise<void>;
  verifyEmail: (user: User) => Promise<void>;
  removeUser: (user: User) => Promise<void>;
  changeEmail: (user: User, newEmail: string) => Promise<void>;
  changePassword: (user: User, newPassword: string) => Promise<void>;
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
}

// Create the auth context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  isUsingMockAuth: false,
  signIn: () => Promise.reject(new Error("Auth context not initialized")),
  signUp: () => Promise.reject(new Error("Auth context not initialized")),
  signOut: () => Promise.reject(new Error("Auth context not initialized")),
  resetPassword: () => Promise.reject(new Error("Auth context not initialized")),
  updateUserProfile: () => Promise.reject(new Error("Auth context not initialized")),
  verifyEmail: () => Promise.reject(new Error("Auth context not initialized")),
  removeUser: () => Promise.reject(new Error("Auth context not initialized")),
  changeEmail: () => Promise.reject(new Error("Auth context not initialized")),
  changePassword: () => Promise.reject(new Error("Auth context not initialized")),
  getToken: () => Promise.reject(new Error("Auth context not initialized")),
});

// Provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Check if auth and app are available
  const authAvailable = auth && typeof auth === 'object';
  const appAvailable = app && typeof app === 'object';
  
  console.log(`AuthProvider mounting, auth available: ${authAvailable} app available: ${appAvailable}`);
  
  // Setup auth state listener
  useEffect(() => {
    const setupAuthListener = async () => {
      try {
        // Safety check: make sure auth object exists and has onAuthStateChanged
        if (!auth || !auth.onAuthStateChanged) {
          throw new Error("Firebase auth is not available. Check your configuration.");
        }
        
        const unsubscribe = onAuthStateChanged(
          auth,
          (authUser) => {
            setUser(authUser);
            setLoading(false);
            setError(null);
          },
          (error) => {
            console.error("Auth state change error:", error);
            setError(error as Error);
            setLoading(false);
          }
        );
        
        // Cleanup function
        return () => unsubscribe();
      } catch (err) {
        console.error("Auth initialization error:", err);
        setError(err as Error);
        setLoading(false);
        // Return a no-op cleanup function
        return () => {};
      }
    };
    
    setupAuthListener();
  }, []);
  
  // Define auth methods that handle errors gracefully
  const signIn = async (email: string, password: string): Promise<UserCredential> => {
    try {
      if (isUsingMockAuth) {
        throw new Error("Authentication service is not available due to invalid API key");
      }
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  };
  
  const signUp = async (email: string, password: string): Promise<UserCredential> => {
    try {
      if (isUsingMockAuth) {
        throw new Error("Authentication service is not available due to invalid API key");
      }
      return await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  };
  
  const signOut = async (): Promise<void> => {
    try {
      if (isUsingMockAuth) {
        // For mock auth, just clear the user state
        setUser(null);
        return;
      }
      await firebaseSignOut(auth);
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };
  
  const resetPassword = async (email: string): Promise<void> => {
    try {
      if (isUsingMockAuth) {
        throw new Error("Authentication service is not available due to invalid API key");
      }
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Reset password error:", error);
      throw error;
    }
  };
  
  const updateUserProfile = async (user: User, displayName?: string, photoURL?: string): Promise<void> => {
    try {
      if (isUsingMockAuth) {
        throw new Error("Authentication service is not available due to invalid API key");
      }
      await updateProfile(user, { displayName, photoURL });
    } catch (error) {
      console.error("Update profile error:", error);
      throw error;
    }
  };
  
  const verifyEmail = async (user: User): Promise<void> => {
    try {
      if (isUsingMockAuth) {
        throw new Error("Authentication service is not available due to invalid API key");
      }
      await sendEmailVerification(user);
    } catch (error) {
      console.error("Verify email error:", error);
      throw error;
    }
  };
  
  const removeUser = async (user: User): Promise<void> => {
    try {
      if (isUsingMockAuth) {
        throw new Error("Authentication service is not available due to invalid API key");
      }
      await deleteUser(user);
    } catch (error) {
      console.error("Remove user error:", error);
      throw error;
    }
  };
  
  const changeEmail = async (user: User, newEmail: string): Promise<void> => {
    try {
      if (isUsingMockAuth) {
        throw new Error("Authentication service is not available due to invalid API key");
      }
      await updateEmail(user, newEmail);
    } catch (error) {
      console.error("Change email error:", error);
      throw error;
    }
  };
  
  const changePassword = async (user: User, newPassword: string): Promise<void> => {
    try {
      if (isUsingMockAuth) {
        throw new Error("Authentication service is not available due to invalid API key");
      }
      await updatePassword(user, newPassword);
    } catch (error) {
      console.error("Change password error:", error);
      throw error;
    }
  };
  
  const getToken = async (forceRefresh?: boolean): Promise<string | null> => {
    try {
      if (isUsingMockAuth || !user) {
        return null;
      }
      return await getIdToken(user, forceRefresh);
    } catch (error) {
      console.error("Get token error:", error);
      return null;
    }
  };
  
  // Provide auth context with all methods
  const value = {
    user,
    loading,
    error,
    isUsingMockAuth,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateUserProfile,
    verifyEmail,
    removeUser,
    changeEmail,
    changePassword,
    getToken,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {/* Show a banner when using mock auth due to invalid API key */}
      {isUsingMockAuth ? (
        <>
          <div
            style={{
              background: "#f8d7da",
              color: "#721c24",
              padding: "10px",
              textAlign: "center",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9999
            }}
          >
            ⚠️ Firebase authentication is not available. {error?.message?.includes('configuration-not-found') 
              ? 'Authentication service needs to be enabled in Firebase Console.' 
              : 'API key or configuration issue.'}
            {' '}Please check <a href="/firebase-test" style={{textDecoration: 'underline', fontWeight: 'bold'}}>Firebase Test Page</a> for diagnostics.
          </div>
          <div className="flex min-h-screen flex-col pt-10">
            {children}
          </div>
        </>
      ) : (
        <div className="flex min-h-screen flex-col">
          {children}
        </div>
      )}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export const useAuth = () => useContext(AuthContext); 
